import React, {useCallback, useEffect, useMemo, useState} from "react"
import {BackButton} from "../../components/BackButton/BackButton"
import {useHistory, useParams} from "react-router-dom"
import {useFormik} from "formik"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import {Helmet} from "react-helmet-async"
import {Button, Card, Checkbox, Form, Input, Table, Tag} from "antd"
import {getPSLeakDevice, updateSettingsPSLeakDevice} from "../../store/psLeakDeviceReducer"
import {Loading} from "../../components/Loading/Loading"
import {getAttributesVersion, setAttributesVersion} from "../../store/attributesVersionReducer"
import moment from "moment"
import {center} from "../../helps/tableHelper"
import {GroupVersionAttributeType} from "../AttributesVersion/JsonAttributes"
import {OptionType} from "../../types/types"
import {FormSelect} from "../../components/Form/FormSelect"
import {FormInputNumber} from "../../components/Form/FormInputNumber"

export const PSLeakDeviceUpdate = () =>
{
    const dispatch = useDispatch()
    const history = useHistory()
    const id = parseInt(useParams<{ id: string }>()?.id, 10)
    const status = useSelector((state: StateType) => state.appReducer.status)
    const device = useSelector((state: StateType) => state.psLeakDeviceReducer.psLeakDevice)
    const version = useSelector((state: StateType) => state.attributesVersionReducer.version)
    const [saveAsLocalAttributes, setSaveAsLocalAttributes] = useState(false)
    const controller = useMemo(() => new AbortController(), [id])

    useEffect(() =>
    {
        if (id) dispatch(getPSLeakDevice({id, controller}))

        return () =>
        {
            dispatch(setAttributesVersion(null))
            controller.abort()
        }
    }, [dispatch, id, controller])

    useEffect(() =>
    {
        if (device && !version) dispatch(getAttributesVersion({id: device.versionLocal, controller}))
    }, [dispatch, device, version, controller])

    const formik = useFormik({
        initialValues: {} as any,
        onSubmit: async (values) =>
        {
            if (device)
            {
                for (const key in values)
                {
                    if (!values[key]) values[key] = 0
                }

                const {payload}: any = await dispatch(updateSettingsPSLeakDevice({
                    id: device.id,
                    attributesServer: JSON.stringify(values) || "{}",
                    saveAsLocalAttributes,
                    controller,
                }))
                if (payload?.status === 0) history.goBack()
            }
        },
    })
    const {values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue, setValues} = formik

    const columns = useMemo(() =>
    {
        return [
            {
                title: "Название",
                dataIndex: "value",
                key: "value",
                align: center,
            },
            {
                title: "Локальные настройки",
                dataIndex: "localValue",
                key: "localValue",
                align: center,
            },
            {
                title: "Настройки сервера",
                dataIndex: "serverValue",
                key: "serverValue",
                align: center,
                render: (i: any) =>
                {
                    if (i?.time) return i?.time
                    if (i?.key) return i.options?.length > 0
                        ? <FormSelect value={values[i?.key]}
                                      onChange={(value) => setFieldValue(i?.key, value)}
                                      options={i.options}
                                      className="form-select"
                                      containerStyle={{width: "100%", marginBottom: 0}}
                                      allowClear
                                      disabled={saveAsLocalAttributes}
                        />
                        : <FormInputNumber value={values[i?.key]}
                                           placeholder="По умолчанию"
                                           name={i?.key}
                                           onBlur={handleBlur}
                                           onChange={(value) => setFieldValue(i?.key, value)}
                                           showErrorMessage={false}
                                           containerStyle={{width: "100%", marginBottom: 0}}
                                           disabled={saveAsLocalAttributes}
                        />
                },
            },
        ]
    }, [values, saveAsLocalAttributes])

    const data = useMemo(() =>
        {
            if (!device || !version) return null

            const parsedServer: { [key: string]: string | number } = JSON.parse(device.attributesServer)
            const parsedLocal: { [key: string]: string | number } = JSON.parse(device.attributesLocal)
            const parsedVersionAttributes: Array<GroupVersionAttributeType> = JSON.parse(version.attributes)

            setValues({})

            const dict: Record<number, {
                groupId: number,
                groupName: string,
                groupIndex: number,
                attrs: Array<{
                    index: number,
                    key: string,
                    value: string,
                    defaultValue: string | number
                    serverValue: { key?: string | number, time?: string, options: Array<OptionType> }
                    localValue: string | number
                }>
            }> = []

            dict[0] = {
                groupId: 0,
                groupName: "Без группы",
                groupIndex: 0,
                attrs: [],
            }

            parsedVersionAttributes?.forEach(g =>
            {
                dict[g.id] = {
                    groupId: g.id,
                    groupName: g.name,
                    groupIndex: g.index,
                    attrs: [],
                }

                g.attributes.forEach(a =>
                {
                    const attr: any = {
                        index: a.index,
                        key: a.key,
                        value: a.value,
                        defaultValue: a.defaultValue,
                        serverValue: {key: a.key, options: a.options ?? []},
                    }

                    if (a.key in parsedLocal)
                    {
                        attr.localValue = a.options?.find(f => f.value === parsedLocal[a.key])?.label ?? parsedLocal[a.key]
                        delete parsedLocal[a.key]
                    }

                    dict[g.id].attrs.push(attr)

                    if (parsedServer[a.key] === null || parsedServer[a.key] === undefined) setFieldValue(a.key, a.defaultValue)
                    else setFieldValue(a.key, parsedServer[a.key])
                })
            })

            const wtfGroupId = -999

            if (Object.keys(parsedLocal).length > 0)
            {
                dict[wtfGroupId] = {
                    groupId: -999,
                    groupName: "Неизвестно",
                    groupIndex: 999,
                    attrs: [],
                }
            }

            for (const key in parsedLocal)
            {
                dict[wtfGroupId].attrs.push({
                    index: 999,
                    key: key,
                    value: key,
                    defaultValue: "",
                    localValue: parsedLocal[key],
                    serverValue: {key, options: []},
                })

                if (parsedServer[key] === null || parsedServer[key] === undefined) setFieldValue(key, "")
                else setFieldValue(key, parsedServer[key])
            }

            return {
                time: {
                    key: "time",
                    value: "Дата",
                    localValue: moment(device.attributesLocalChangeTime).format("DD.MM.YYYY HH:mm"),
                    serverValue: {time: moment(device.attributesServerChangeTime).format("DD.MM.YYYY HH:mm")},
                },
                items: Object
                    .values(dict)
                    .filter(i => i.attrs?.length > 0)
                    .sort((a, b) => a.groupIndex - b.groupIndex),
            }
        },
        [device, version])

    const copyLocalAttributes = useCallback(() =>
    {
        if (device?.attributesLocal)
        {
            const parsed = JSON.parse(device.attributesLocal)
            if (parsed) setValues(parsed)
        }
    }, [device])

    if (!device || !version) return <Loading/>

    return (
        <div className="form-page">
            <Helmet>
                <title>{`Обновление атрибутов ${device?.name}, ${device?.nameObject}`}</title>
            </Helmet>
            <Form className="form-container"
                  style={{width: "auto"}}
                  autoComplete="off"
                  onSubmitCapture={handleSubmit}
            >
                <div className="description-header"><BackButton onClick={() => history.goBack()}/></div>
                <div className="description-title">{`Обновление атрибутов ${device?.name}, ${device?.nameObject}`}</div>

                <Card variant="borderless"
                      className="criclebox tablespace mb-24"
                      title={
                          <div className="table-title">
                              <Button onClick={copyLocalAttributes}
                                      type="link" size="middle"
                                      style={{marginTop: 5}}
                                      disabled={saveAsLocalAttributes}>
                                  Скопировать локальные
                              </Button>
                          </div>
                      }
                      extra={<div>Версия {device.versionLocal}</div>}
                >
                    <div className="table-responsive">
                        {
                            data ?
                                <div>
                                    <Table columns={columns}
                                           dataSource={[data?.time]}
                                           pagination={false}
                                           bordered
                                           className="ant-border-space no-hover"
                                           style={{margin: "20px 10px"}}
                                    />

                                    {data?.items?.map(i =>
                                    {
                                        return (
                                            <Table key={i.groupId}
                                                   columns={columns}
                                                   title={() => <div style={{fontSize: 16, fontWeight: "bold", margin: "auto"}}>{i.groupName}</div>}
                                                   dataSource={i.attrs.sort((a, b) => a.index - b.index)}
                                                   pagination={false}
                                                   bordered
                                                   className="ant-border-space no-hover"
                                                   style={{margin: "20px 10px"}}
                                            />
                                        )
                                    })}
                                </div>
                                : <Loading/>
                        }
                    </div>
                </Card>

                <div style={{marginTop: 20}}>
                    <Checkbox checked={saveAsLocalAttributes}
                              name="saveAsLocalAttributes"
                              onChange={() =>
                              {
                                  setSaveAsLocalAttributes(prev => !prev)
                                  copyLocalAttributes()
                              }}
                    >
                        <Tag color={saveAsLocalAttributes ? "green" : "default"}>Сохранить локальные настройки</Tag>
                    </Checkbox>
                </div>

                <div className="form-button-block">
                    <Button type="primary"
                            htmlType="submit"
                            loading={status === "loading"}
                    >
                        Обновить
                    </Button>
                    <Button type="default"
                            onClick={() => history.goBack()}
                    >
                        Отмена
                    </Button>
                </div>
            </Form>
        </div>
    )
}

